import { getDocs, query, where, addDoc, serverTimestamp, updateDoc, doc } from "firebase/firestore";
import { CustomNarratorsRef, db } from "../../../firebase";

/**
 * Fetch all custom narrators created by a specific user
 * @param {string} userId - The user ID to filter by (createdBy field)
 * @returns {Promise<Array>} Array of custom narrator documents
 */
export const getCustomNarratorsByUser = async (userId) => {
  try {
    if (!userId) {
      console.warn("getCustomNarratorsByUser: userId is required");
      return [];
    }

    const q = query(CustomNarratorsRef, where("createdBy", "==", userId));
    const querySnapshot = await getDocs(q);

    const customNarrators = [];
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      customNarrators.push({
        id: docSnap.id,
        ...data,
        // If updatedAt is missing, default it to createdAt (for display convenience)
        updatedAt: data.updatedAt ?? data.createdAt ?? null,
      });
    });

    return customNarrators;
  } catch (error) {
    console.error("Error fetching custom narrators:", error.message);
    return [];
  }
};

/**
 * Create a new custom narrator
 * @param {string} name - The name of the narrator
 * @param {string} userId - The user ID who created this narrator (createdBy field)
 * @returns {Promise<Object>} The created narrator document
 */
export const createCustomNarrator = async (name, userId) => {
  try {
    if (!name || !userId) {
      console.warn("createCustomNarrator: name and userId are required");
      return null;
    }

    const newNarrator = {
      name: name.trim(),
      createdBy: userId,
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(CustomNarratorsRef, newNarrator);
    
    return {
      id: docRef.id,
      ...newNarrator,
    };
  } catch (error) {
    console.error("Error creating custom narrator:", error.message);
    return null;
  }
};

/**
 * Check if a narrator name already exists in custom narrators for a user
 * @param {string} name - The narrator name to check
 * @param {Array} customNarrators - Array of custom narrator objects
 * @returns {boolean} True if the name exists, false otherwise
 */
export const narratorNameExists = (name, customNarrators) => {
  if (!name || !customNarrators || customNarrators.length === 0) {
    return false;
  }
  
  const normalizedName = name.trim().toLowerCase();
  return customNarrators.some(
    narrator => narrator.name.toLowerCase() === normalizedName
  );
};

/**
 * Fetch a single custom narrator by name for a specific user
 * @param {string} name - The name of the narrator
 * @param {string} userId - The user ID who created this narrator
 * @returns {Promise<Object|null>} The narrator document or null if not found
 */
export const getCustomNarratorByName = async (name, userId) => {
  try {
    if (!name || !userId) {
      console.warn("getCustomNarratorByName: name and userId are required");
      return null;
    }

    const q = query(
      CustomNarratorsRef,
      where("name", "==", name.trim()),
      where("createdBy", "==", userId)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    };
  } catch (error) {
    console.error("Error fetching custom narrator by name:", error.message);
    return null;
  }
};

/**
 * Fetch a single custom narrator by name for a specific organization.
 *
 * This supports org-wide sharing of custom narrators.
 *
 * @param {string} name - The name of the narrator
 * @param {string} organizationId - The organization id to filter by (organization field)
 * @returns {Promise<Object|null>} The narrator document or null if not found
 */
export const getCustomNarratorByNameAndOrganization = async (name, organizationId) => {
  try {
    if (!name || !organizationId) {
      console.warn("getCustomNarratorByNameAndOrganization: name and organizationId are required");
      return null;
    }

    const q = query(
      CustomNarratorsRef,
      where("name", "==", name.trim()),
      where("organization", "==", organizationId)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    };
  } catch (error) {
    console.error("Error fetching custom narrator by name and organization:", error.message);
    return null;
  }
};

/**
 * Fetch a single custom narrator by name without scoping to a user or organization.
 *
 * WARNING: This is intentionally broad and should only be used as a last-resort
 * fallback in admin contexts.
 *
 * @param {string} name
 * @returns {Promise<Object|null>}
 */
export const getCustomNarratorByNameUnscoped = async (name) => {
  try {
    if (!name) {
      console.warn("getCustomNarratorByNameUnscoped: name is required");
      return null;
    }

    const q = query(CustomNarratorsRef, where("name", "==", name.trim()));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    };
  } catch (error) {
    console.error("Error fetching custom narrator by name (unscoped):", error.message);
    return null;
  }
};

/**
 * Update a custom narrator document
 * @param {string} docId - The ID of the custom narrator document to update
 * @param {Object} data - The data to update
 * @returns {Promise<void>}
 */
export const updateCustomNarrator = async (docId, data) => {
  try {
    if (!docId || !data) {
      console.warn("updateCustomNarrator: docId and data are required");
      return;
    }
    const docRef = doc(db, "customNarrators", docId);
    await updateDoc(docRef, data);
  } catch (error) {
    console.error("Error updating custom narrator:", error.message);
    throw error;
  }
};
